// ============================================================
// 認証ストア — JWT トークンとユーザー情報を管理
// ============================================================

import { create } from 'zustand'
import { safeLocalStorage } from '@/lib/safeStorage'

export interface AuthUser {
  id: number
  email: string
  name: string
  isSystemAdmin: boolean
}

/** JWT のペイロード部（Base64url）をデコードしてユーザー情報を取得
 *  atob は Latin-1 バイト列として扱うため、日本語などの多バイト文字を含む場合は
 *  TextDecoder で UTF-8 として再解釈する必要がある。 */
function decodeToken(token: string): AuthUser | null {
  try {
    const payload = token.split('.')[1]
    // Base64url → Base64 変換（JWT は + → -, / → _ に置換されている）
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes)) as AuthUser
  } catch {
    return null
  }
}

const STORAGE_KEY = 'auth_token'

/** 初期トークンを取得（URL フラグメント #token= を優先、なければ localStorage）
 *  OAuth コールバック後に /#token=<jwt> でリダイレクトされるため、
 *  useEffect より先に同期的に処理してレースコンディションを防ぐ。
 *  フラグメントはサーバーに送信されないためクエリパラメータより安全。 */
function getInitialToken(): string | null {
  const hash = window.location.hash
  if (hash.startsWith('#token=')) {
    const urlToken = hash.slice('#token='.length)
    safeLocalStorage.setItem(STORAGE_KEY, urlToken)
    // フラグメントだけ除去（クエリは保持。例：/player/X?action=protect#token=... → /player/X?action=protect）
    window.history.replaceState({}, '', window.location.pathname + window.location.search)
    return urlToken
  }
  return safeLocalStorage.getItem(STORAGE_KEY)
}

interface AuthState {
  token: string | null
  user: AuthUser | null
  setToken: (token: string) => void
  updateName: (name: string) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>(() => {
  const token = getInitialToken()
  return {
    token,
    user: token ? decodeToken(token) : null,
    setToken: (token: string) => {
      safeLocalStorage.setItem(STORAGE_KEY, token)
      useAuthStore.setState({ token, user: decodeToken(token) })
    },
    updateName: (name: string) => {
      useAuthStore.setState((s) => ({
        user: s.user ? { ...s.user, name } : null,
      }))
    },
    logout: () => {
      safeLocalStorage.removeItem(STORAGE_KEY)
      useAuthStore.setState({ token: null, user: null })
    },
  }
})
